import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import { loadStripe } from "@stripe/stripe-js";
import * as actions from "../../../../redux/actions/billing";
import { showToast } from "../../../../helper/ShowToast";
import { createFullEnhancedMix } from "../../../../redux/actions/audioEngineer";
import { authorizeAndRegisterUrStudioHold, cancelUrStudioHoldAfterFailure } from "../../../../helper/urstudioPaymentHold";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export const MixEnhancerPackModal = ({ isOpen, onClose, onPaymentSuccess, requestData, getEnhanceRequestData, validateEnhanceRequest, setIsProcessing }) => {
    const dispatch = useDispatch();
    const { audioEngineerPlans, hasActiveSubscription } = useSelector((state) => state.billing);
    const [planLoading, setPlanLoading] = useState(false)

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [customerSessionClientSecret, setCustomerSessionClientSecret] = useState(null);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);

    // Prevent repeated fetching
    const hasFetchedPlans = useRef(false);
    useEffect(() => {
        if (isOpen && !hasFetchedPlans.current) {
            hasFetchedPlans.current = true;
            if(requestData?.stemProcessing) {
                dispatch(actions.getPlanList("audio_engineer_pack", setPlanLoading, "", "mix_enhancer_stems"));
            } else { 
                dispatch(actions.getPlanList("audio_engineer_pack", setPlanLoading, "", "mix_enhancer"));
            }
        } else if (!isOpen) {
            hasFetchedPlans.current = false;
        }
    }, [isOpen, dispatch]);

    const allMixEnhancerPlans = audioEngineerPlans?.[0] || {}

    //Continue to Stripe payment
    const handleContinue = async () => {
        if (!selectedPlan) return toast.warning("Please select a plan");

        try {
            setLoading(true);
            const data = {
                planId: allMixEnhancerPlans?.id,
                priceId: selectedPlan.priceId,
                quantity: 1
            };
            const res = await dispatch(actions.initCustomerSession(data, setLoading, toast));

            if (res?.clientSecret && res?.customerSessionClientSecret) {
                setClientSecret(res.clientSecret);
                setCustomerSessionClientSecret(res.customerSessionClientSecret);
                setStep(2);
            } else {
                showToast('error', "Failed to initialize Stripe session")
            }
        } catch (err) {
            console.error("Customer session error:", err);
            showToast('error', "Stripe initialization failed")
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
        setSelectedPlan(null);
        setClientSecret(null);
        setCustomerSessionClientSecret(null);
        setStep(1);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-auto">
            <div className="bg-gradient-to-br from-[#000456] via-[#0a1a8a] to-[#1e3a8a] rounded-2xl shadow-lg w-full max-w-[800px] mx-2 my-10">
                <div className="modal-body relative px-4 md:px-6 py-8">
                    <button
                        onClick={handleClose}
                        className="absolute top-3 right-4 text-theme_clr2 hover:text-theme_clr4 text-xl"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>

                    {/* Header */}
                    <div className="text-center mb-6">
                        <h2 className="text-2xl font-bold text-white mb-2">
                            Purchase your Enhanced Mix from our Mix Enhancer
                        </h2>
                        <p className="text-base text-gray-300 mb-4 max-w-3xl mx-auto">
                            {allMixEnhancerPlans?.description || "Select a plan to continue"}
                        </p>
                    </div>

                    {/* Step 1 — Select Plan */}
                    {step === 1 && (
                        <>
                            <div className="flex flex-wrap justify-center gap-4 mb-6">
                                {planLoading || !allMixEnhancerPlans?.prices?.length ? (
                                    [...Array(1)].map((_, index) => (
                                        <div
                                            key={index}
                                            className="w-full max-w-[250px] h-[140px] rounded-xl border-2 border-white/10 bg-gradient-to-r from-[#1e1e3f] via-[#2a2a5f] to-[#1e1e3f] animate-pulse"
                                        >
                                            <div className="h-4 bg-white/10 rounded mt-4 mx-8"></div>
                                            <div className="h-6 bg-white/10 rounded mt-3 mx-8"></div>
                                            <div className="h-4 bg-white/10 rounded mt-3 mx-8"></div>
                                        </div>
                                    ))
                                ) : (
                                    allMixEnhancerPlans?.prices?.map((plan) => {
                                        const isActive = selectedPlan?.priceId === plan.priceId;
                                        return (
                                            <button
                                                key={plan.priceId}
                                                onClick={() => setSelectedPlan(plan)}
                                                disabled={loading}
                                                className={`w-full max-w-[250px] rounded-xl p-5 border-2 transition-all text-white text-center ${isActive
                                                    ? "border-[#FA6138] bg-[#FA6138]/20 shadow-[0_0_0_3px_rgba(250,97,56,0.2)]"
                                                    : "border-white/20 hover:border-[#FA6138]/60"
                                                    }`}
                                            >
                                                <div className="text-sm opacity-80 mb-1">{plan.label}</div>
                                                <div className="text-2xl font-extrabold">${plan.price.toFixed(2)}</div>
                                                <div className="text-sm mt-2 text-gray-300">
                                                    {plan.priceDescription || "One-time purchase"}
                                                </div>
                                            </button>
                                        );
                                    }))}
                            </div>

                            {/* Continue button */}
                            <div className="flex justify-center">
                                <button
                                    type="button"
                                    disabled={!selectedPlan || loading}
                                    onClick={handleContinue}
                                    // mt-6 w-full max-w-md bg-[#FA6138] text-white py-3 rounded-xl hover:bg-[#ff724d] transition-colors font-medium
                                    className={`px-6 py-3 rounded-lg font-bold text-white transition-all ${!selectedPlan || loading
                                        ? "bg-gray-500 cursor-not-allowed"
                                        : "bg-theme_clr2 hover:bg-[#e55532]"
                                        }`}
                                >
                                    {loading ? (
                                        <span className="inline-flex items-center gap-2">
                                            <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                            Initializing…
                                        </span>
                                    ) : (
                                        <>Continue to Payment →</>
                                    )}
                                </button>
                            </div>
                        </>
                    )}

                    {/* Step 2 — Stripe Payment Form */}
                    {step === 2 && clientSecret && customerSessionClientSecret && (
                        <Elements
                            stripe={stripePromise}
                            options={{
                                clientSecret,
                                customerSessionClientSecret,
                                layout: { type: "tabs", defaultCollapsed: false, overflow: "auto" },
                                appearance: { theme: "flat", variables: { colorPrimary: "#FA6138" } },
                                loader: "always",
                            }}
                        >
                            <MixEnhancerPaymentForm
                                selectedPlan={selectedPlan}
                                planId={allMixEnhancerPlans?.id}
                                serviceKey={requestData?.stemProcessing ? "mix_enhancer_stems" : "mix_enhancer"}
                                onClose={handleClose}
                                onPaymentSuccess={onPaymentSuccess}
                                setStep={setStep}
                                setLoading={setLoading}
                                loading={loading}
                                requestData={requestData}
                                getEnhanceRequestData={getEnhanceRequestData}
                                validateEnhanceRequest={validateEnhanceRequest}
                                dispatch={dispatch}
                                setIsLoading={setIsProcessing}
                            />
                        </Elements>
                    )}
                </div>
            </div>
        </div>
    );
};

// CHILD COMPONENT — Payment Form 
const MixEnhancerPaymentForm = ({ selectedPlan, planId, serviceKey, onClose, onPaymentSuccess, setStep, setLoading, loading, requestData, getEnhanceRequestData, validateEnhanceRequest, dispatch, setIsLoading }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [selectedMethod, setSelectedMethod] = useState(null);
    const { hasActiveSubscription } = useSelector((state) => state.billing);

    // Track selected method
    useEffect(() => {
        if (!elements) return;
        const paymentElement = elements.getElement("payment");
        if (!paymentElement) return;

        const handleChange = (event) => {
            setSelectedMethod(event.value?.type || null);
        };
        paymentElement.on("change", handleChange);
        return () => paymentElement.off("change", handleChange);
    }, [elements]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setLoading(true);
        let authorizedPaymentIntentId = null;
        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: window.location.origin + "/admin/urstudio/mix-enhancer" },
                redirect: "if_required",
            });

            let resolvedPi = paymentIntent;
            if (error) {
                const existingPi = error.payment_intent;
                if (existingPi && (existingPi.status === "requires_capture" || existingPi.status === "succeeded")) {
                    resolvedPi = existingPi;
                } else {
                    showToast('error', "Payment failed. Please try again.")
                    return;
                }
            }

            const requestBody = getEnhanceRequestData?.() || requestData;
            const validationError = validateEnhanceRequest?.(requestBody);
            if (validationError) {
                showToast('error', validationError);
                return;
            }

            authorizedPaymentIntentId = resolvedPi?.id || null;

            const result = await authorizeAndRegisterUrStudioHold({
                dispatch,
                paymentIntent: resolvedPi,
                planId,
                priceId: selectedPlan.priceId,
                serviceKey: serviceKey || "mix_enhancer",
                quantity: 1,
                mixesUploadId: requestBody?.id || null,
                taskId: requestBody?.taskId || null,
                operationName: "retrieveEnhancedTrack",
            });

            if (!result.ok) {
                showToast('error', "Payment could not be completed. Any hold was released — please try again.");
                return;
            }

            showToast('success', "Payment on hold — creating your enhanced mix. You'll only be charged when it's ready.")
            await dispatch(createFullEnhancedMix({
                ...requestBody,
                paymentIntentId: resolvedPi.id,
            }, setIsLoading))
            setTimeout(() => {
                if (onPaymentSuccess) {
                    onPaymentSuccess();
                }
                onClose();
                setStep(1);
            }, 1500);
        } catch (err) {
            if (authorizedPaymentIntentId) {
                await cancelUrStudioHoldAfterFailure(
                    dispatch,
                    authorizedPaymentIntentId,
                    err?.message || "Enhance job failed to start after payment"
                );
            }
            showToast('error', err?.response?.data?.message || err?.message || "Something went wrong while confirming payment.")
        } finally {
            setLoading(false);
        }
    }, [stripe, elements, setLoading, dispatch, requestData, getEnhanceRequestData, validateEnhanceRequest, setIsLoading, onPaymentSuccess, onClose, setStep, planId, selectedPlan, serviceKey]);
    return (
        <form onSubmit={handleSubmit} className="bg-white/5 p-6 rounded-xl border border-white/10 space-y-4">
            <h3 className="text-lg text-white font-semibold">
                Pay ${selectedPlan.price * 1} for Mix Enhancer Access
            </h3>
            {!hasActiveSubscription && (
                <p className="text-base text-gray-300 mt-2">
                    <span className="text-theme_clr2 font-bold text-lg mr-2">|</span>
                    Upgrade to <strong>UpNComer Pro</strong> and get up to <strong>50% off audio engineering tools</strong>, plus access to all modules and AI credits every month.
                </p>
            )}
            {selectedMethod && (
                <p className="text-sm text-gray-300">
                    Paying with: <span className="font-semibold">{selectedMethod}</span>
                </p>
            )}
            <PaymentElement />
            <button
                type="submit"
                disabled={!stripe || loading}
                className={`w-full mt-6 py-3 rounded-lg text-lg font-bold text-white transition-all ${loading ? "bg-gray-500 cursor-not-allowed" : "bg-[#FA6138] hover:bg-[#e55532]"
                    }`}
            >
                {loading ? (
                    <div className="flex justify-center items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                    </div>
                ) : (
                    `Complete Payment — $${selectedPlan.price * 1}`
                )}
            </button>
        </form>
    );
};
